import React, { useEffect, useState } from 'react';
import { getAnalytics } from '../api/client';

const Analytics: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [hoveredPeriod, setHoveredPeriod] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    getAnalytics(days)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 15 }}>
      Загрузка...
    </div>
  );
  if (!data) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#ef4444', fontSize: 15 }}>
      Ошибка загрузки
    </div>
  );

  const scoreDiff = data.avg_score - data.prev_period_avg_score;

  const metrics = [
    {
      icon: '\u{1F3AF}',
      label: 'Средний балл',
      value: data.avg_score,
      extra: `${scoreDiff >= 0 ? '+' : ''}${scoreDiff.toFixed(1)} vs прошлый период`,
      extraColor: scoreDiff >= 0 ? '#10b981' : '#ef4444',
    },
    {
      icon: '\u{1F4AC}',
      label: 'Диалогов',
      value: data.total_dialogues,
      extra: null,
      extraColor: '',
    },
    {
      icon: '\u23F1',
      label: 'Ср. время ответа',
      value: `${data.avg_response_time} мин`,
      extra: null,
      extraColor: '',
    },
    {
      icon: '\u{1F4B0}',
      label: 'Упущенная выручка',
      value: data.missed_revenue ? data.missed_revenue.toLocaleString() + ' \u20BD' : '0 \u20BD',
      extra: null,
      extraColor: '',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Аналитика</h1>
          <p style={styles.subtitle}>Детальный анализ работы команды</p>
        </div>
        <div style={styles.periodSelector}>
          {[7, 30, 90].map((d) => {
            const isActive = days === d;
            const isHovered = hoveredPeriod === d;
            return (
              <button
                key={d}
                onClick={() => setDays(d)}
                onMouseEnter={() => setHoveredPeriod(d)}
                onMouseLeave={() => setHoveredPeriod(null)}
                style={{
                  ...styles.periodBtn,
                  ...(isActive ? styles.periodBtnActive : {}),
                  ...(!isActive && isHovered ? styles.periodBtnHover : {}),
                }}
              >
                {d} дней
              </button>
            );
          })}
        </div>
      </div>

      {/* Metrics */}
      <div style={styles.metricsGrid}>
        {metrics.map((m, i) => (
          <div key={i} style={styles.metricCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={styles.metricLabel}>{m.label}</div>
              <span style={{ fontSize: 20 }}>{m.icon}</span>
            </div>
            <div style={styles.metricValue}>{m.value}</div>
            {m.extra && (
              <div style={{ fontSize: 13, fontWeight: 600, color: m.extraColor, marginTop: 6 }}>
                {m.extra}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Improvements */}
      {data.improvements?.length > 0 && (
        <div style={{
          ...styles.card,
          background: 'linear-gradient(135deg, #ecfdf5 0%, #fff 50%)',
          border: '1px solid #a7f3d0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>\u2705</span>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#059669', margin: 0 }}>
              Что улучшилось
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
            {data.improvements.map((item: string, i: number) => (
              <div key={i} style={{
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.7)',
                borderRadius: 10,
                fontSize: 14,
                color: '#065f46',
                lineHeight: 1.5,
                border: '1px solid #d1fae5',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <span style={{ color: '#10b981', fontWeight: 700 }}>\u2191</span>
                {item}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issues */}
      {data.issues?.length > 0 && (
        <div style={{
          ...styles.card,
          background: 'linear-gradient(135deg, #fef2f2 0%, #fff 50%)',
          border: '1px solid #fecaca',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>\u26A0\uFE0F</span>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#dc2626', margin: 0 }}>
              Что нужно улучшить
            </h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
            {data.issues.map((item: string, i: number) => (
              <div key={i} style={{
                padding: '10px 16px',
                background: 'rgba(255,255,255,0.7)',
                borderRadius: 10,
                fontSize: 14,
                color: '#991b1b',
                lineHeight: 1.5,
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <span style={{ color: '#ef4444', fontWeight: 700 }}>\u2192</span>
                {item}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 36px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: 15,
    color: '#94a3b8',
    margin: '4px 0 0 0',
  },
  periodSelector: {
    display: 'flex',
    gap: 6,
    background: '#f1f5f9',
    padding: 4,
    borderRadius: 12,
  },
  periodBtn: {
    padding: '8px 18px',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: '#64748b',
    transition: 'all 0.15s ease',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  periodBtnActive: {
    background: '#fff',
    color: '#6366f1',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  periodBtnHover: {
    background: 'rgba(255,255,255,0.5)',
    color: '#475569',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 24,
  },
  metricCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  metricLabel: {
    fontSize: 13,
    color: '#94a3b8',
    fontWeight: 500,
    marginBottom: 10,
  },
  metricValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.5px',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: 20,
  },
};

export default Analytics;
